import { Box, xcss } from '@atlaskit/primitives';
import React, { type RefObject, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { useDisplayParticipants } from '../hooks/useDisplayParticipants.tsx';
import { type Participant } from '../types.ts';

const NAMEPLATE_HEIGHT_IN_PIXELS = 24;
const BORDER_WIDTH_IN_PIXELS = 2;

type PresenceNameplateAppearance = 'default' | 'ai-agent';

const getNameplateStyles = ({ position = 'top' }: { position?: 'top' | 'bottom' }) => {
  return xcss({
    position: 'absolute',
    top: `-${NAMEPLATE_HEIGHT_IN_PIXELS - BORDER_WIDTH_IN_PIXELS}px`,
    height: `${NAMEPLATE_HEIGHT_IN_PIXELS}px`,
    width: 'max-content',
    zIndex: 'navigation',
    borderTopLeftRadius: position === 'top' ? '3px' : '',
    borderTopRightRadius: position === 'top' ? '3px' : '',
    borderBottomLeftRadius: position === 'bottom' ? '3px' : '',
    borderBottomRightRadius: position === 'bottom' ? '3px' : '',
    paddingTop: 'space.025',
    paddingBottom: 'space.025',
    paddingLeft: 'space.075',
    paddingRight: 'space.075',
    color: 'color.text.inverse',
    display: 'flex',
    alignItems: 'center'
  });
};

const aiAgentNameplateStyles = xcss({
  boxSizing: 'border-box',
  borderStyle: 'solid',
  borderWidth: 'border.width',
  background: `linear-gradient(var(--ds-text, #292a2e), var(--ds-text, #292a2e)) padding-box,
    conic-gradient(
      from 267deg at 50% 50%,
      #1868db 0deg 103deg,
      #fca700 103deg 183deg,
      #bf63f3 183deg 294deg,
      #82b536 294deg 360deg
    ) border-box`
});

const Nameplate = ({
  participants,
  position = 'top',
  appearance = 'default'
}: {
  participants: Participant[];
  position?: 'top' | 'bottom';
  appearance?: PresenceNameplateAppearance;
}) => {
  const backgroundColor = participants?.[0]?.colorToken;
  const participantString = useDisplayParticipants(participants);
  const isAiAgent = appearance === 'ai-agent';

  return (
    <Box
      xcss={
        isAiAgent
          ? [getNameplateStyles({ position }), aiAgentNameplateStyles]
          : getNameplateStyles({ position })
      }
      backgroundColor={isAiAgent ? undefined : backgroundColor}
      style={isAiAgent ? { borderColor: 'transparent' } : undefined}
      testId="presence-nameplate"
      data-appearance={appearance}
    >
      <Box xcss={nameStyles}>{participantString}</Box>
    </Box>
  );
};

const nameStyles = xcss({
  minWidth: '0px',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  flexShrink: 1,
  flexGrow: 1
});

export const PresenceNameplate = ({
  participants,
  anchorRef,
  position = 'top',
  appearance = 'default'
}: {
  participants: Participant[];
  anchorRef: RefObject<HTMLElement>;
  position?: 'top' | 'bottom';
  appearance?: PresenceNameplateAppearance;
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const scrollTimeout = useRef<ReturnType<typeof globalThis.setTimeout> | null>(null);

  // To avoid a jumpy experience when scrolling, we hide the nameplate while scrolling
  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(false);
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
      scrollTimeout.current = globalThis.setTimeout(() => {
        setIsVisible(true);
      }, 150);
    };
    globalThis.addEventListener('scroll', handleScroll, true);
    return () => {
      globalThis.removeEventListener('scroll', handleScroll, true);
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
    };
  }, []);

  if (!anchorRef.current) {
    return null;
  }
  const rect = anchorRef.current.getBoundingClientRect();
  return (
    isVisible &&
    participants.length > 0 &&
    createPortal(
      <div
        style={{
          position: 'absolute',
          // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
          top:
            position === 'top'
              ? rect.top
              : rect.bottom + NAMEPLATE_HEIGHT_IN_PIXELS - BORDER_WIDTH_IN_PIXELS,
          // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
          // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
          left: rect.left + BORDER_WIDTH_IN_PIXELS,
          // Prevent the portal from intercepting mouse events on the
          // underlying cell.
          pointerEvents: 'none'
        }}
      >
        <Nameplate
          participants={participants}
          position={position}
          appearance={appearance}
        />
      </div>,
      document.body
    )
  );
};
